import type { FC } from 'react';
import { useCallback, useState } from 'react';

import type { LoginCredentials } from '@opendoor/auth-fe';
import { Box, Text, TextInput } from '@opendoor/bricks-next';
import { Form } from '@opendoor/bricks/core';
import { GoogleAuth } from '@opendoor/cinderblocks/platform/auth/GoogleAuth';

import { useAuthAuthenticated } from 'platform/auth-fe';
import { Button, Link } from 'platform/bricks-next';

import { COSMOS_DOMAIN, GOOGLE_CLIENT_ID } from 'shared/helpers/globals';
import { useLazyAsyncFn } from 'shared/hooks/useLazyAsyncFn';

interface ReauthFormProps {
  onSuccess: () => void;
  title?: string;
}

/** UI for the user to reauthenticate. Commonly used before allowing the user to view sensitive data. */
const ReauthFormWithoutContext: FC<
  ReauthFormProps & Pick<ReturnType<typeof useAuthAuthenticated>, 'user' | 'verifyLogin'>
> = ({ onSuccess, title, user, verifyLogin: _verifyLogin }) => {
  const [_verifyLoginLazy, verifyLoginResult] = useLazyAsyncFn(_verifyLogin);
  const verifyLogin = useCallback(
    async (credentials: LoginCredentials) => {
      const result = await _verifyLoginLazy(credentials);
      if (result.success) {
        onSuccess();
      } // errors handled with loginLastCall
    },
    [_verifyLoginLazy, onSuccess],
  );

  const [password, setPassword] = useState('');

  const loginFailed = verifyLoginResult.data && !verifyLoginResult.data.success;

  return (
    <Box gap="$12x" maxWidth={344}>
      <Box gap="$8x">
        <Text tag="h2" typography="$headerSmall">
          {title || 'Sign in again to continue'}
        </Text>
        <Text tag="p" typography="$bodySmall">
          Signing in as{' '}
          <Link
            variant="secondary"
            size="small"
            analyticsName="user-email"
            href={`mailto:${user.email}`}
          >
            {user.email}
          </Link>
        </Text>
      </Box>
      <Form
        onSubmit={(event) => {
          event.preventDefault();
          void verifyLogin({ email: user.email, password });
        }}
      >
        <Box gap="$12x">
          <Box width="100%">
            <TextInput
              analyticsName="cake-reauthform-password"
              id="reauth-password"
              size="small"
              onChangeText={(value) => setPassword(value || '')}
              required
              textContentType="password"
              title="Password"
              errorText={
                loginFailed
                  ? 'Incorrect email or password. Use the link below to reset your password'
                  : ''
              }
            />
          </Box>
          <Box width="100%" gap="$4x">
            <Button
              variant="primary"
              size="medium"
              type="submit"
              analyticsName="reauth-submit"
              label="Confirm"
              aria-label="Confirm and submit password"
              loading={verifyLoginResult?.loading}
              disabled={verifyLoginResult?.loading || password.length === 0}
            />
          </Box>
        </Box>
      </Form>
      <GoogleAuth
        clientId={GOOGLE_CLIENT_ID}
        onAuthed={(response) => void verifyLogin({ googleIdToken: response.tokenId })}
      />
      <Box alignItems="center">
        <Link
          variant="primary"
          size="medium"
          href={`${COSMOS_DOMAIN}/reset-password`}
          target="_blank"
          analyticsName="re-auth-forgot-password"
          aria-label="Opens the forgot password page"
        >
          Forgot your password?
        </Link>
      </Box>
    </Box>
  );
};

export const ReauthForm: FC<ReauthFormProps> = ({ onSuccess, title }) => {
  const { user, verifyLogin } = useAuthAuthenticated();
  return (
    <ReauthFormWithoutContext
      onSuccess={onSuccess}
      title={title}
      user={user}
      verifyLogin={verifyLogin}
    />
  );
};
